import React, { MouseEventHandler, useEffect, useState } from "react";
import {
  Form,
  FormControl,
  FormField,
  FormLabel,
  FormItem,
  FormMessage,
} from "../../../components/ui/form";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import { Button } from "../../../components/ui/button";
import { Skeleton } from "../../../components/ui/skeleton";
import { Textarea } from "../../../components/ui/textarea";
import { searchBrandDrugNames } from "../../../services/brandDrugNameService";
import { getLengths } from "../../../services/frequencyLength";
import { getFrequencies } from "../../../services/frequencyService";
import { addNewPrescriptionAppointment } from "../../../services/prescriptionAppointmentService";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { IoMdAdd } from "react-icons/io";

export default function BrandNamePrescription({
  appointmentId,
  getPrescriptionData,
  onAdded,
}: {
  appointmentId: string | undefined;
  getPrescriptionData: any;
  onAdded?: () => void;
}) {
  // Search state and options for DrugName select
  const [drugSearch, setDrugSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [drugOptions, setDrugOptions] = useState<any[]>([]);
  const [loadingDrugs, setLoadingDrugs] = useState(false);
  // Search state and options for Length select
  const [lengthOptions, setLengthOptions] = useState<any[]>([]);
  const [lengthSearchTerm, setLengthSearchTerm] = useState("");
  const [loadingLengths, setLoadingLengths] = useState(true);
  // Search state and options for Frequency select
  const [frequencyOptions, setFrequencyOptions] = useState<any[]>([]);
  const [frequencySearchTerm, setFrequencySearchTerm] = useState("");
  const [loadingFrequencies, setLoadingFrequencies] = useState(true);
  // Load initial drug options on mount
  useEffect(() => {
    setLoadingDrugs(true);
    searchBrandDrugNames(null)
      .then((data) => {
        // Expecting array of { label, value } or { name, id }
        const opts = Array.isArray(data)
          ? data.map((d: any) => ({
              name: d.name,
              contents: d.contents,
              value: d.id,
            }))
          : [];
        setDrugOptions(opts);
      })
      .catch(() => setDrugOptions([]))
      .finally(() => setLoadingDrugs(false));
  }, []);

  // Load initial length options on mount
  useEffect(() => {
    getLengths()
      .then((data) => {
        setLengthOptions(data);
      })
      .catch(() => setLengthOptions([]))
      .finally(() => setLoadingLengths(false));
  }, []);

  // Load initial frequency options on mount
  useEffect(() => {
    getFrequencies()
      .then((data) => {
        setFrequencyOptions(data);
      })
      .catch(() => setFrequencyOptions([]))
      .finally(() => setLoadingFrequencies(false));
  }, []);

  // Debounce search term to set drugSearch
  useEffect(() => {
    const timer = setTimeout(() => {
      setDrugSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Refetch drug options on search (3+ letters or cleared)
  useEffect(() => {
    if (drugSearch === "" || drugSearch.length < 3) {
      // setLoadingDrugs(true);
      searchBrandDrugNames(null)
        .then((data) => {
          const opts = Array.isArray(data)
            ? data.map((d: any) => ({
                name: d.name,
                contents: d.contents,
                value: d.id,
              }))
            : [];
          setDrugOptions(opts);
        })
        .catch(() => setDrugOptions([]));
      // .finally(() => setLoadingDrugs(false));
      return;
    }
    // Search for 3+ letters
    // setLoadingDrugs(true);
    searchBrandDrugNames(drugSearch)
      .then((data) => {
        const opts = Array.isArray(data)
          ? data.map((d: any) => ({
              name: d.name,
              contents: d.contents,
              value: d.id,
            }))
          : [];
        setDrugOptions(opts);
      })
      .catch(() => setDrugOptions([]));
    // .finally(() => setLoadingDrugs(false));
  }, [drugSearch]);
  const form = useForm();

  const onSubmit = (data: any) => {
    if (!data.newDrugName) {
      form.setError("newDrugName", { message: "Please select a drug name" });
      return;
    }
    if (!data.length) {
      form.setError("length", { message: "Please select a length" });
      return;
    }
    if (!data.frequency) {
      form.setError("frequency", { message: "Please select a frequency" });
      return;
    }
    addNewPrescriptionAppointment({
      appointmentId: appointmentId!,
      drugNameId: data.newDrugName,
      frequencyId: data.frequency,
      lengthId: data.length,
      notes: data.notes || undefined,
    })
      .then(() => {
        toast.success("Prescription added successfully");
        form.reset(); // Reset the form fields
        setSearchTerm(""); // Reset search terms
        setLengthSearchTerm("");
        setFrequencySearchTerm("");
        onAdded?.(); // Let the parent close the Add dialog, if any
      })
      .catch((error) => {
        toast.error("There was an error adding prescription");
        // Optionally show error message
      })
      .finally(() => {
        getPrescriptionData(appointmentId!);
      });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          name="newDrugName"
          render={({ field }) => {
            return (
              <FormItem className="flex flex-col md:flex-row md:items-center md:gap-4">
                <FormLabel className="md:w-1/3">
                  <h2>Select Brand Drug Name </h2>
                </FormLabel>
                <FormControl className="md:w-2/3">
                  {loadingDrugs ? (
                    <Skeleton className="h-10 w-full" />
                  ) : (
                    <Select
                      value={field.value ?? ""}
                      onValueChange={field.onChange}
                      disabled={loadingDrugs}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            loadingDrugs
                              ? "Loading..."
                              : "Select drug name (start typing 3 or more letters)"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <div className="px-2 py-1">
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            placeholder="Search drug name..."
                            className="w-full rounded border px-2 py-1 text-sm mb-2 bg-white"
                          />
                        </div>
                        {loadingDrugs ? (
                          <div className="px-2 py-1 text-muted-foreground text-sm">
                            Loading...
                          </div>
                        ) : drugOptions.length === 0 ? (
                          <div className="px-2 py-1 text-muted-foreground text-sm">
                            No results
                          </div>
                        ) : (
                          drugOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              <span style={{ fontWeight: "bold" }}>
                                {opt.name}
                              </span>{" "}
                              -{" "}
                              <span style={{ fontFamily: "monospace" }}>
                                ({opt.contents})
                              </span>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  )}
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <FormField
          name="length"
          render={({ field }) => {
            return (
              <FormItem className="flex flex-col md:flex-row md:items-center md:gap-4">
                <FormLabel className="md:w-1/3">
                  <h2>Select Prescription Length</h2>
                </FormLabel>
                <FormControl className="md:w-2/3">
                  {loadingLengths ? (
                    <Skeleton className="h-10 w-full" />
                  ) : (
                    <Select value={field.value ?? ""} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select prescription length" />
                      </SelectTrigger>
                      <SelectContent>
                        <div className="px-2 py-1">
                          <input
                            type="text"
                            value={lengthSearchTerm}
                            onChange={(e) =>
                              setLengthSearchTerm(e.target.value)
                            }
                            onKeyDown={(e) => e.stopPropagation()}
                            placeholder="Search length..."
                            className="w-full rounded border px-2 py-1 text-sm mb-2 bg-white"
                          />
                        </div>
                        {(() => {
                          const filtered = lengthOptions.filter(
                            (opt) =>
                              opt.name
                                .toLowerCase()
                                .includes(lengthSearchTerm.toLowerCase()) ||
                              opt.description
                                .toLowerCase()
                                .includes(lengthSearchTerm.toLowerCase())
                          );
                          return filtered.length === 0 ? (
                            <div className="px-2 py-1 text-muted-foreground text-sm">
                              No results
                            </div>
                          ) : (
                            filtered.map((opt) => (
                              <SelectItem key={opt.id} value={opt.id}>
                                <span style={{ fontWeight: "bold" }}>
                                  {opt.name}
                                </span>{" "}
                                -{" "}
                                <span style={{ fontFamily: "monospace" }}>
                                  ({opt.description})
                                </span>
                              </SelectItem>
                            ))
                          );
                        })()}
                      </SelectContent>
                    </Select>
                  )}
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <FormField
          name="frequency"
          render={({ field }) => {
            return (
              <FormItem className="flex flex-col md:flex-row md:items-center md:gap-4">
                <FormLabel className="md:w-1/3">
                  <h2>Select Prescription Frequency</h2>
                </FormLabel>
                <FormControl className="md:w-2/3">
                  {loadingFrequencies ? (
                    <Skeleton className="h-10 w-full" />
                  ) : (
                    <Select value={field.value ?? ""} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select prescription frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        <div className="px-2 py-1">
                          <input
                            type="text"
                            value={frequencySearchTerm}
                            onChange={(e) =>
                              setFrequencySearchTerm(e.target.value)
                            }
                            onKeyDown={(e) => e.stopPropagation()}
                            placeholder="Search frequency..."
                            className="w-full rounded border px-2 py-1 text-sm mb-2 bg-white"
                          />
                        </div>
                        {(() => {
                          const filtered = frequencyOptions.filter(
                            (opt) =>
                              opt.name
                                .toLowerCase()
                                .includes(frequencySearchTerm.toLowerCase()) ||
                              opt.translation
                                .toLowerCase()
                                .includes(frequencySearchTerm.toLowerCase()) ||
                              opt.description
                                .toLowerCase()
                                .includes(frequencySearchTerm.toLowerCase())
                          );
                          return filtered.length === 0 ? (
                            <div className="px-2 py-1 text-muted-foreground text-sm">
                              No results
                            </div>
                          ) : (
                            filtered.map((opt) => (
                              <SelectItem key={opt.id} value={opt.id}>
                                <span style={{ fontWeight: "bold" }}>
                                  {opt.name}
                                </span>{" "}
                                ({opt.translation} {"->"} {opt.description})
                              </SelectItem>
                            ))
                          );
                        })()}
                      </SelectContent>
                    </Select>
                  )}
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <FormField
          name="notes"
          render={({ field }) => {
            return (
              <FormItem className="flex flex-col md:flex-row md:items-start md:gap-4">
                <FormLabel className="md:w-1/3">
                  <h2>Notes</h2>
                </FormLabel>
                <FormControl className="md:w-2/3">
                  <div className="relative">
                    <Textarea
                      value={field.value || ""}
                      onChange={field.onChange}
                      maxLength={255}
                      placeholder="Enter notes..."
                      className="resize-none"
                    />
                    <div className="absolute bottom-2 right-2 text-sm text-muted-foreground">
                      {field.value?.length || 0}/255
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <Button className="my-4 bg-green-700 " type="submit">
          <IoMdAdd /> Add Prescription
        </Button>
      </form>
    </Form>
  );
}
